<!--
/*
 * Компонент ShareReferralButton — кнопка + bottom sheet для шаринга реферальной ссылки
 */
-->
<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useClipboard } from '@vueuse/core'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useI18n } from 'vue-i18n'

interface IShareReferralButtonProps {
  iconOnly?: boolean
}

withDefaults(defineProps<IShareReferralButtonProps>(), {
  iconOnly: false,
})

const userStore = useUserStore()
const { t } = useI18n()
const { copy } = useClipboard()

const sheetVisible = ref(false)
const copied = ref(false)

const shareText = computed(() => {
  return t('profile.referralDesc')
})

const refLink = computed(() => userStore.user?.referralLink ?? '')

const encodedText = computed(() => encodeURIComponent(shareText.value))
const encodedUrl = computed(() => encodeURIComponent(refLink.value))

const socialOptions = computed(() => [
  {
    icon: 'ri-whatsapp-line',
    label: 'WhatsApp',
    color: 'success',
    url: `https://wa.me/?text=${encodedText.value}%0A${encodedUrl.value}`,
  },
  {
    icon: 'ri-twitter-x-line',
    label: 'X',
    color: 'secondary',
    url: `https://twitter.com/intent/tweet?text=${encodedText.value}%0A${encodedUrl.value}`,
  },
])

const shareTelegram = () => {
  const url = `https://t.me/share/url?url=${encodedUrl.value}&text=${encodedText.value}`
  window.open(url, '_blank')
  sheetVisible.value = false
}

const openShare = (url: string) => {
  window.open(url, '_blank')
  sheetVisible.value = false
}

const copyLink = async () => {
  await copy(refLink.value)
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}
</script>

<template>
  <v-btn
    v-if="iconOnly"
    icon="ri-share-forward-line"
    variant="text"
    color="on-surface"
    size="small"
    @click="sheetVisible = true"
  />

  <v-btn
    v-else
    variant="flat"
    color="surface"
    class="text-on-surface"
    rounded="lg"
    block
    append-icon="ri-share-forward-line"
    @click="sheetVisible = true"
  >
    {{ t('profile.inviteFriend') }}
  </v-btn>

  <v-bottom-sheet v-model="sheetVisible">
    <v-card class="rounded-t-xl w-100 share-sheet-card">
      <div class="d-flex flex-column ga-4 pa-4">
        <div class="d-flex align-center justify-space-between">
          <span class="text-h5">{{ t('profile.share') }}</span>
          <v-btn
            icon="ri-close-line"
            variant="text"
            size="small"
            @click="sheetVisible = false"
          />
        </div>

        <div class="d-flex justify-space-around">
          <div
            class="d-flex flex-column align-center ga-1 cursor-pointer"
            @click="shareTelegram"
          >
            <v-avatar color="primary" size="48" variant="tonal">
              <i class="ri-telegram-line ri-lg" />
            </v-avatar>
            <span class="text-caption">Telegram</span>
          </div>

          <div
            v-for="option in socialOptions"
            :key="option.label"
            class="d-flex flex-column align-center ga-1 cursor-pointer"
            @click="openShare(option.url)"
          >
            <v-avatar :color="option.color" size="48" variant="tonal">
              <i :class="[option.icon, 'ri-lg']" />
            </v-avatar>
            <span class="text-caption">{{ option.label }}</span>
          </div>
        </div>

        <v-btn
          variant="tonal"
          block
          :color="copied ? 'success' : 'primary'"
          :prepend-icon="copied ? 'ri-check-line' : 'ri-link'"
          @click="copyLink"
        >
          {{ copied ? t('profile.copiedExclamation') : t('profile.copyLink') }}
        </v-btn>
      </div>
    </v-card>
  </v-bottom-sheet>
</template>

<style scoped>
.share-sheet-card {
  max-width: var(--app-max-width);
  margin: 0 auto;
}
</style>
